import { DataGrid } from 'components/shared/data/DataGrid/DataGrid';
import {
  defaultColumn,
  statisticDetailsColumns,
  statisticDetailsGroupingColumn,
} from 'components/reports/SchoolStatDetails/columns';
import { StatDetailsRow } from 'store/reports/reports.types';
import { useStyles } from 'components/reports/SchoolStatDetails/SchoolStatDetails.styles';
import { DataGridApi } from 'components/shared/data/DataGrid/DataGrid.types';
import React, { useMemo } from 'react';
import { formatSpecialValues, getProcessedTableData } from 'components/reports/SchoolStatDetails/helpers';
import { StatDetailsTable } from 'store/reports/reports.types';
import { useTranslator } from 'components/shared/hooks';
import { Paper } from 'components/shared/surfaces/Paper/Paper';

interface StatGridProps {
  isLoading: boolean;
  table: StatDetailsTable | null;
  setGridApi: React.Dispatch<any>;
  statName: string;
}

export const StatGrid = ({ isLoading, table, setGridApi, statName }: StatGridProps) => {
  const classes = useStyles();
  const trans = useTranslator();

  const onGridReady = ({ agGridApi }: DataGridApi) => {
    setGridApi(agGridApi);
  };

  const effectiveColumns = useMemo(() => {
    if (!table?.columns) return [];
    return table.columns;
  }, [table?.columns]);

  const { gridItems, groupRows } = useMemo(() => {
    if (!table?.data || !effectiveColumns.length) {
      return { gridItems: [], groupRows: [] };
    }
    const { leafs, headers } = getProcessedTableData(table.data, effectiveColumns);
    return {
      gridItems: formatSpecialValues(leafs, trans),
      groupRows: formatSpecialValues(headers, trans),
    };
  }, [table?.data, effectiveColumns, trans]);

  const groupingColumnName = useMemo(() => {
    if (!effectiveColumns.length) return undefined;
    return (
      effectiveColumns
        .filter((c) => c.group)
        .map((c) => c.name)
        .filter(Boolean)
        .join(' / ') || undefined
    );
  }, [effectiveColumns]);

  if (!table) {
    return null;
  }

  return (
    <Paper innerSpacing={2} square className={classes.paper}>
      <DataGrid<StatDetailsRow>
        columns={statisticDetailsColumns({
          columns: effectiveColumns,
          trans,
          statName,
          // Keep grouping source columns hidden in grid body to avoid duplicated
          // "Wszystkie placówki / Szkoła" values on leaf rows.
          hideGroupColumns: true,
        })}
        data={gridItems}
        groupingColumn={statisticDetailsGroupingColumn({ name: groupingColumnName, statName })}
        defaultColumn={defaultColumn()}
        onGridReady={onGridReady}
        suppressAggFuncInHeader={true}
        isLoading={isLoading}
        icons={{
          groupExpanded: '<span class="statGroupIcon statGroupIcon--expanded" aria-hidden="true"></span>',
          groupContracted: '<span class="statGroupIcon statGroupIcon--contracted" aria-hidden="true"></span>',
        }}
        context={{ rowGroupValues: groupRows }}
      />
      <div className="pt-3 text-muted text-right">{trans('REPORTS.SCHOOL_STAT_DETAILS.FIXED_VALUES_EXPLANATION')}</div>
    </Paper>
  );
};
